import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TbFlag, TbSend } from 'react-icons/tb';
import { communityApi as defaultApi } from './community-api';

function publicMediaUrl(path) {
  if (!path) return '';
  return path.startsWith('/media/') ? path : `/media/${path.replace(/^\/+/, '')}`;
}

export function MemberAvatar({ person, className, label }) {
  const name = person && person.username;
  const avatarUrl = publicMediaUrl(person && person.avatarPath);
  if (avatarUrl) return <img className={className} src={avatarUrl} alt={label || `Foto de perfil de ${name}`} />;
  return <span className={`${className} community-avatar--fallback`} aria-hidden="true">{(name && name.charAt(0).toUpperCase()) || '?'}</span>;
}

export function ProfileAvatarLink({ person, className, linkClassName = 'community-avatar-link', onNavigate }) {
  const id = person && (person.id || person.userId);
  const avatar = <MemberAvatar person={person} className={className} />;
  if (!id) return avatar;
  return <Link className={linkClassName} to={`/comunidad/perfil/${id}`} aria-label={`Ver perfil de ${person.username || 'socio'}`} onClick={onNavigate}>{avatar}</Link>;
}

export function PostCard({ post, api = defaultApi, onModerated, isAdmin = false, highlightCommentId, viewer }) {
  const [item, setItem] = useState(post);
  const [comments, setComments] = useState(post.recentComments || post.comments || []);
  const [nextCommentsCursor, setNextCommentsCursor] = useState(post.nextCommentsCursor || null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportError, setReportError] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  // Moderación (admin): hoja propia de la app (antes un diálogo nativo del navegador); `hideTarget` es { kind: 'post' } o { kind: 'comment', entry }.
  const [hideTarget, setHideTarget] = useState(null);
  const [hideReason, setHideReason] = useState('');
  const [hideError, setHideError] = useState('');
  const [hiding, setHiding] = useState(false);
  const hideSheetRef = useRef(null);
  const hideFieldRef = useRef(null);
  const hideCancelRef = useRef(null);
  const hideOpenerRef = useRef(null);
  const hideOpen = hideTarget !== null;
  useEffect(() => {
    if (!hideOpen) return undefined;
    const opener = hideOpenerRef.current;
    if (hideFieldRef.current) hideFieldRef.current.focus();
    return () => { if (opener && opener.isConnected) opener.focus(); };
  }, [hideOpen]);
  const reportInFlight = useRef(false);
  async function toggleReaction() {
    const previous = item;
    setItem({ ...item, viewerReacted: !item.viewerReacted, reactionsCount: Math.max(0, (item.reactionsCount || 0) + (item.viewerReacted ? -1 : 1)) });
    try { await api.react(item.id); } catch (caught) { setItem(previous); setError(caught.message || 'No se pudo reaccionar.'); }
  }
  async function addComment(event) {
    event.preventDefault();
    if (!comment.trim()) return;
    try {
      const created = await api.addComment(item.id, comment.trim());
      const author = created.author || (viewer ? { id: viewer.id, username: viewer.username, avatarPath: viewer.avatarPath } : undefined);
      setComments(current => [{ ...created, ...(author ? { author } : {}), body: comment.trim() }, ...current]);
      setComment('');
    }
    catch (caught) { setError(caught.message || 'No se pudo comentar.'); }
  }
  function openReport(target) {
    setReportTarget(target);
    setReportReason('');
    setReportError('');
    setReportSent(false);
  }
  async function submitReport(event) {
    event.preventDefault();
    const reason = reportReason.trim();
    if (!reason || reason.length > 500 || !reportTarget || reportInFlight.current) return;
    reportInFlight.current = true;
    setSendingReport(true);
    setReportError('');
    try {
      await api.createReport({ ...reportTarget, reason });
      setReportSent(true);
      setReportReason('');
    } catch (caught) {
      setReportError(caught.message || 'No se pudo enviar la denuncia.');
    } finally {
      reportInFlight.current = false;
      setSendingReport(false);
    }
  }
  async function loadMoreComments() {
    if (!nextCommentsCursor || loadingComments) return;
    setLoadingComments(true);
    try {
      const page = await api.comments(item.id, nextCommentsCursor);
      setComments(current => {
        const ids = new Set(current.map(entry => entry.id).filter(Boolean));
        const older = page.comments.filter(entry => !entry.id || !ids.has(entry.id));
        return [...older, ...current];
      });
      setNextCommentsCursor(page.nextCursor);
    } catch (caught) { setError(caught.message || 'No se pudieron cargar más comentarios.'); }
    finally { setLoadingComments(false); }
  }
  function openHide(target, opener) {
    hideOpenerRef.current = opener || document.activeElement;
    setHideTarget(target);
    setHideReason('');
    setHideError('');
  }
  function closeHide() {
    if (!hiding) setHideTarget(null);
  }
  async function submitHide(event) {
    event.preventDefault();
    const reason = hideReason.trim();
    if (!reason || reason.length > 300 || !hideTarget || hiding) return;
    if (hideCancelRef.current) hideCancelRef.current.focus();
    setHiding(true);
    setHideError('');
    try {
      if (hideTarget.kind === 'post') {
        const result = await api.hidePost(item.id, { reason });
        setItem(current => ({ ...current, hiddenAt: result.hiddenAt || new Date().toISOString() }));
        if (onModerated) onModerated();
      } else {
        const entryId = hideTarget.entry.id;
        const result = await api.hideComment(entryId, { reason });
        setComments(current => current.map(commentItem => commentItem.id === entryId ? { ...commentItem, hiddenAt: result.hiddenAt || new Date().toISOString() } : commentItem));
      }
      setHideTarget(null);
    } catch (caught) {
      setHideError(caught.message || (hideTarget.kind === 'post' ? 'No se pudo moderar la publicación.' : 'No se pudo moderar el comentario.'));
    } finally {
      setHiding(false);
    }
  }
  async function togglePostVisibility(event) {
    if (!item.hiddenAt) { openHide({ kind: 'post' }, event && event.currentTarget); return; }
    try {
      await api.unhidePost(item.id);
      setItem(current => ({ ...current, hiddenAt: null }));
      if (onModerated) onModerated();
    } catch (caught) {
      setError(caught.message || 'No se pudo moderar la publicación.');
    }
  }
  async function toggleCommentVisibility(entry, event) {
    if (!entry.hiddenAt) { openHide({ kind: 'comment', entry }, event && event.currentTarget); return; }
    try {
      await api.unhideComment(entry.id);
      setComments(current => current.map(commentItem => commentItem.id === entry.id ? { ...commentItem, hiddenAt: null } : commentItem));
    } catch (caught) {
      setError(caught.message || 'No se pudo moderar el comentario.');
    }
  }
  const author = item.author || {};
  return <article className="gym-card community-post" aria-label={`Publicación de ${author.username || 'socio'}`}>
    <header className="community-post__header">
      <ProfileAvatarLink person={author} className="community-post__avatar" />
      <Link className="community-post__author" to={`/comunidad/perfil/${author.id || author.userId || ''}`}>{author.username || 'Socio'}</Link>
      {isAdmin && <button type="button" className={`gym-button ${item.hiddenAt ? 'gym-button--secondary' : 'gym-button--danger'} community-post__moderation-action`} onClick={togglePostVisibility}>{item.hiddenAt ? 'Mostrar publicación' : 'Ocultar publicación'}</button>}
      <button type="button" className="gym-button gym-button--secondary community-post__action" onClick={() => openReport({ postId: item.id })}><TbFlag aria-hidden="true" focusable="false" />Denunciar</button>
    </header>
    {item.body && <p className="community-post__body">{item.body}</p>}
    {item.media && item.media.length > 0 && <div className="community-post__media">{item.media.map(media => {
      const path = media.path || '';
      const src = path.startsWith('/media/') ? path : `/media/${path.replace(/^\/+/, '')}`;
      return <img key={media.id || path} src={src} alt="Foto de la publicación" />;
    })}</div>}
    <div className="community-post__reaction-row"><button type="button" className={`gym-button gym-button--secondary community-post__reaction${item.viewerReacted ? ' community-post__reaction--active' : ''}`} onClick={toggleReaction} aria-pressed={!!item.viewerReacted} aria-label={`♥ ${item.reactionsCount || 0}`}><span aria-hidden="true">{item.viewerReacted ? '♥' : '♡'}</span><span>{item.reactionsCount || 0}</span></button></div>
    {comments.length > 0 && <ul className="community-post__comments">{comments.map((entry, index) => <li key={entry.id || index} className={`community-post__comment${entry.id === highlightCommentId ? ' community-post__comment--highlighted' : ''}`}>
      {entry.author && <ProfileAvatarLink person={entry.author} className="community-comment__avatar" />}
      <div className="community-comment__copy">
        {entry.author?.username && <span className="community-comment__author">{entry.author.username}</span>}
        <p>{entry.hiddenAt ? 'Comentario oculto' : entry.body}</p>
      </div>
      {entry.id && <button type="button" className="gym-button gym-button--secondary community-post__action" onClick={() => openReport({ commentId: entry.id })}><TbFlag aria-hidden="true" focusable="false" />Denunciar</button>}
      {isAdmin && entry.id && <button type="button" className={`gym-button ${entry.hiddenAt ? 'gym-button--secondary' : 'gym-button--danger'} community-post__moderation-action`} onClick={(event) => toggleCommentVisibility(entry, event)}>{entry.hiddenAt ? 'Mostrar comentario' : 'Ocultar comentario'}</button>}
    </li>)}</ul>}
    {reportTarget && <div className="community-report-backdrop"><form className="gym-form community-post__report community-report-sheet" onSubmit={submitReport}>
      <label className="gym-field" htmlFor={`report-reason-${item.id}`}>
        Motivo de la denuncia
        <textarea className="gym-input" id={`report-reason-${item.id}`} value={reportReason} maxLength={500} required onChange={event => { setReportReason(event.target.value); setReportSent(false); }} />
      </label>
      <span className="community-report__counter">{reportReason.length}/500</span>
      {reportError && <p className="gym-form__error" role="alert">{reportError}</p>}
      {reportSent && <p className="gym-state" role="status">Denuncia enviada.</p>}
      <button type="submit" className="gym-button gym-button--primary" disabled={sendingReport || !reportReason.trim() || reportReason.trim().length > 500}>{sendingReport ? 'Enviando…' : 'Enviar denuncia'}</button>
      <button type="button" className="gym-button gym-button--secondary" disabled={sendingReport} onClick={() => setReportTarget(null)}>Cancelar denuncia</button>
    </form></div>}
    {hideTarget && <div className="community-report-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeHide(); }}>
      <form
        ref={hideSheetRef}
        className="gym-form community-report-sheet community-hide-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`hide-title-${item.id}`}
        onSubmit={submitHide}
        onKeyDown={event => {
          if (event.key === 'Escape') { event.preventDefault(); closeHide(); return; }
          if (event.key !== 'Tab' || !hideSheetRef.current) return;
          const focusable = Array.from(hideSheetRef.current.querySelectorAll('button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
          if (focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <h2 id={`hide-title-${item.id}`} className="community-hide-sheet__title">{hideTarget.kind === 'post' ? 'Ocultar publicación' : 'Ocultar comentario'}</h2>
        <label className="gym-field" htmlFor={`hide-reason-${item.id}`}>
          Motivo
          <textarea ref={hideFieldRef} className="gym-input" id={`hide-reason-${item.id}`} value={hideReason} maxLength={300} required disabled={hiding} onChange={event => setHideReason(event.target.value)} />
        </label>
        <span className="community-report__counter">{hideReason.length}/300</span>
        {hideError && <p className="gym-form__error" role="alert">{hideError}</p>}
        <button type="submit" className="gym-button gym-button--danger community-hide-sheet__confirm" disabled={hiding || !hideReason.trim()}>{hiding ? 'Ocultando…' : (hideTarget.kind === 'post' ? 'Ocultar publicación' : 'Ocultar comentario')}</button>
        <button ref={hideCancelRef} type="button" className="gym-button gym-button--secondary" onClick={closeHide}>Cancelar</button>
      </form>
    </div>}
    {nextCommentsCursor && <button type="button" className="gym-button gym-button--secondary" onClick={loadMoreComments} disabled={loadingComments}>{loadingComments ? 'Cargando…' : 'Ver más comentarios'}</button>}
    <form className="gym-form community-post__comment-form" onSubmit={addComment}><label className="gym-sr-only" htmlFor={`comment-${item.id}`}>Comentar</label><input className="gym-input" id={`comment-${item.id}`} placeholder="Escribí un comentario" value={comment} onChange={event => setComment(event.target.value)} /><button className="gym-button gym-button--primary" type="submit" disabled={!comment.trim()}><TbSend aria-hidden="true" focusable="false" />Comentar</button></form>
    {error && <p className="gym-form__error" role="alert">{error}</p>}
  </article>;
}
